import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Flex, Text, spaces, Country, COUNTRIES } from '@electricjs/arc';

import { useResponsiveness } from '@common';

import formatDate from '@/helpers/formatDate';
import { useStorefront } from '../../StorefrontProvider';
import { useCurrentCatalogEstimatedDates } from '../../utils/useCurrentCatalogEstimatedDates';
import { useCurrentWarehouseEstimatedDates } from '../../utils/useCurrentWarehouseEstimatedDates';

const StyledText = styled(Text)`
  word-break: break-word;
  hyphens: auto;
`;

export type ShippingReadOnlyProps = {
  useEta?: boolean;
};

const ShippingReadOnly = ({ useEta = false }: ShippingReadOnlyProps) => {
  const { t } = useTranslation('storefront');
  const { hasNoHorizontalSpace } = useResponsiveness();

  const { storefrontBackorderPossibility } = useFlags();

  const {
    storefrontCheckoutForm,
    cartRequiresCatalogShippingMethod,
    cartRequiresWarehouseShippingMethod,
  } = useStorefront();

  const catalogEstimatedDates = useCurrentCatalogEstimatedDates();
  const warehouseEstimatedDates = useCurrentWarehouseEstimatedDates();

  const shippingName = storefrontCheckoutForm?.watch('shippingName') ?? '';
  const shippingPhone = storefrontCheckoutForm?.watch('shippingPhone') ?? '';
  const shippingAddress = storefrontCheckoutForm?.watch('shippingAddress');
  const catalogShippingMethod = storefrontCheckoutForm?.watch(
    'catalogShippingMethod'
  );
  const warehouseShippingMethod = storefrontCheckoutForm?.watch(
    'warehouseShippingMethod'
  );

  return (
    <Flex vertical={hasNoHorizontalSpace} gap={spaces.space_m_rem}>
      {/* Left column - Shipping Address */}
      <Flex vertical flexBasis="50%">
        <Text variant="label-large">
          {t('sections.shipping.readOnly.addressLabel')}
        </Text>
        <StyledText>{shippingName}</StyledText>
        <StyledText>{shippingPhone}</StyledText>
        <StyledText>{shippingAddress?.streetAddress1}</StyledText>
        <StyledText>{shippingAddress?.streetAddress2}</StyledText>
        <StyledText>
          {shippingAddress?.city}, {shippingAddress?.state}{' '}
          {shippingAddress?.zip}
        </StyledText>
        <StyledText>
          {/* not translating country names inside addresses */}
          {COUNTRIES[shippingAddress?.country as Country]?.name}
        </StyledText>
      </Flex>

      {/* Right column - Shipping Methods */}
      {(cartRequiresCatalogShippingMethod ||
        cartRequiresWarehouseShippingMethod) && (
        <Flex vertical flexBasis="50%" rowGap={spaces.space_m_rem}>
          {/* Warehouse shipping method */}
          {cartRequiresWarehouseShippingMethod && (
            <Flex vertical width="100%">
              <Text variant="label-large">
                {t('sections.shipping.readOnly.warehouseMethodLabel')}
              </Text>
              <StyledText>{warehouseShippingMethod?.label}</StyledText>
              <StyledText>
                {t('sections.shipping.readOnly.shipsBy')}{' '}
                {formatDate(warehouseEstimatedDates?.shipDate?.toISOString())}
              </StyledText>
            </Flex>
          )}

          {/* Catalog shipping method */}
          {cartRequiresCatalogShippingMethod && (
            <Flex vertical width="100%">
              <Text variant="label-large">
                {cartRequiresWarehouseShippingMethod
                  ? t('sections.shipping.readOnly.purchasesMethodLabel')
                  : t('sections.shipping.readOnly.methodLabel')}
              </Text>
              <StyledText>{catalogShippingMethod?.label}</StyledText>
              {/* "eta" is only used for quote checkout */}
              {useEta ? (
                <StyledText>
                  {t('sections.shipping.readOnly.arrivesBetween', {
                    minDate: formatDate(
                      catalogShippingMethod?.eta?.min?.toISOString()
                    ),
                    maxDate: formatDate(
                      catalogShippingMethod?.eta?.max?.toISOString()
                    ),
                  })}
                </StyledText>
              ) : (
                !storefrontBackorderPossibility && (
                  <StyledText>
                    {t('sections.shipping.readOnly.shipsBy')}{' '}
                    {formatDate(catalogEstimatedDates?.shipDate?.toISOString())}
                  </StyledText>
                )
              )}
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ShippingReadOnly;
